import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export type CardVariant = 'default' | 'compact' | 'expanded';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly variant?: CardVariant;
  /** Optional media slot rendered above the card body (image, CourtScene, etc.). */
  readonly media?: ReactNode;
  /** Optional footer slot rendered below the body, separated by a hairline. */
  readonly footer?: ReactNode;
  /** Render the card as a clickable surface (subtle hover lift). */
  readonly interactive?: boolean;
  /** Named primary action, exposed as a real keyboard/touch button. */
  readonly onPress?: () => void;
  readonly pressLabel?: string;
  readonly pressSelected?: boolean;
}

const PADDING: Record<CardVariant, string> = {
  compact: 'p-3',
  default: 'p-4',
  expanded: 'p-5',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    variant = 'default',
    media,
    footer,
    interactive,
    onPress,
    pressLabel,
    pressSelected,
    onClick,
    className,
    children,
    ...rest
  },
  ref,
) {
  const { t } = useI18n();
  const clickable = interactive || !!onPress;
  return (
    <div
      ref={ref}
      className={cn(
        'relative isolate min-w-0 max-w-full rounded-xl bg-surface border border-hairline overflow-hidden',
        clickable &&
          'cursor-pointer transition-[transform,box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-safe:hover:-translate-y-px motion-reduce:transition-none hover:border-hairline-2 hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.10)]',
        className,
      )}
      {...rest}
      onClick={(event) => {
        const target = event.target;
        if (
          target instanceof Element &&
          target !== event.currentTarget &&
          target.closest('a, button, input, select, textarea, [role="button"], [role="link"]')
        )
          return;
        onPress?.();
        onClick?.(event);
      }}
    >
      {onPress ? (
        <button
          type="button"
          aria-label={
            pressLabel ?? t('common:cardUi.openDetails', { defaultValue: 'Open details' })
          }
          aria-pressed={pressSelected}
          onClick={(event) => {
            event.stopPropagation();
            onPress();
          }}
          className="absolute inset-0 z-10 rounded-[inherit] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-text"
        />
      ) : null}
      {media ? <div className="bg-surface-2">{media}</div> : null}
      <div
        className={cn(
          onPress &&
            'relative z-20 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_input]:pointer-events-auto [&_select]:pointer-events-auto [&_textarea]:pointer-events-auto [&_label]:pointer-events-auto [&_[tabindex]]:pointer-events-auto',
          PADDING[variant],
        )}
      >
        {children}
      </div>
      {footer ? (
        <div
          className={cn('border-t border-hairline', onPress && 'relative z-20', PADDING[variant])}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
});
